'use client';

import React, { useContext, useEffect, useState } from 'react';
import { FaArrowTrendUp, FaArrowTrendDown } from 'react-icons/fa6';
import { SessionContext } from '@benzinga/session-context';
import { WatchlistManager, Watchlist } from '@benzinga/watchlist-manager';
import type { InsidersTradeFiling } from '@benzinga/insider-trades-manager';
import { useBenzingaEdge } from '@benzinga/edge';
import { getWatchlistInsiderFilingsFromSecCalendar } from '../../../src/utils/watchlistSecCalendar';
import { BlockLayout } from './BlockLayout';
import { Button, ErrorBoundary, Spinner } from '@benzinga/core-ui';
import { BzDateTime } from '@benzinga/date-utils';
import { formatLarge, formatPrice, toTitleCase } from '@benzinga/utils';

const EDGE_CTA_URL =
  'https://www.benzinga.com/premium/ideas/benzinga-edge-checkout/?utm_source=homepage-insider-trades-widget&adType=paywall&ad=insider-trades-text-ads';

const formatTradeDate = (dateStr: string | undefined) => {
  if (!dateStr) return 'N/A';
  return BzDateTime.from(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

interface TradeCardProps {
  trade: InsidersTradeFiling;
}

const TradeCard = React.memo(({ trade }: TradeCardProps) => {
  const isBuy = trade.traded_shares > 0;
  const TrendIcon = isBuy ? FaArrowTrendUp : FaArrowTrendDown;
  const trendColor = isBuy ? 'text-green-600' : 'text-red-600';
  const percentColor = trade.traded_percentage > 0 ? 'text-green-600' : 'text-red-600';
  const percentBgColor = trade.traded_percentage > 0 ? 'bg-green-50' : 'bg-red-50';

  const sanitizedName = trade.insider_names.replaceAll(/[^a-zA-Z0-9 ]/g, ' ');
  const insiderName = toTitleCase(sanitizedName);
  const insiderCik = trade.insiders?.[0]?.cik;

  return (
    <div
      key={`insider-trades-sidebar-item-${trade._id}`}
      className="block bg-white rounded-lg border border-gray-200 hover:border-gray-300 transition-colors p-3"
    >
      <a href={`/sec/insider-trades/search?company_cik=${trade.company_cik}`}>
        <div className="flex items-baseline gap-2 mb-3">
          <span className="font-semibold text-gray-900 uppercase">{trade.company_ticker || 'N/A'}</span>
          <span className="text-sm text-gray-500 truncate">{trade.company_name}</span>
        </div>
      </a>
      <div className="flex flex-col gap-2 mb-3">
        <div className="flex items-center justify-between bg-slate-100 rounded-md px-3 py-1.5">
          <span className="font-medium text-sm text-gray-600">Qty.</span>
          <span className={`flex items-center gap-1 text-sm font-medium ${trendColor}`}>
            <TrendIcon size={12} />
            {trade.traded_shares ? formatLarge(Math.abs(trade.traded_shares), 1) : 'N/A'}
          </span>
        </div>
        <div className="flex items-center justify-between bg-slate-100 rounded-md px-3 py-2">
          <span className="font-medium text-sm text-gray-600">Price</span>
          <span className="text-sm font-medium text-gray-900">
            {trade.traded_share_price ? `$${formatPrice(trade.traded_share_price)}` : 'N/A'}
          </span>
        </div>
        <div className="flex items-center justify-between bg-slate-100 rounded-md px-3 py-2">
          <span className="font-medium text-sm text-gray-600">Size</span>
          <span className={`flex items-center gap-1 text-sm font-medium ${trendColor}`}>
            <TrendIcon size={12} />
            {trade.traded_value ? `$${formatLarge(Math.abs(trade.traded_value), 1)}` : 'N/A'}
          </span>
        </div>
      </div>
      <div className="flex items-center justify-between mb-3">
        <div className="flex-1 min-w-0">
          <a href={`/sec/insider-trades/${insiderCik}/${insiderName}`}>
            <div className="text-sm font-semibold text-gray-900 truncate mb-1">{trade.insider_names || 'Unknown'}</div>
          </a>
          <div className="text-xs text-gray-500 truncate">
            {trade.insider_titles_unique || trade.insider_titles || 'N/A'}
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-xs text-gray-500 mb-1">Own</div>
          <div className="flex items-center gap-1.5">
            <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${percentColor} ${percentBgColor}`}>
              {trade.traded_percentage ? `${trade.traded_percentage.toFixed(1)}%` : '-'}
            </span>
            <span className="text-sm font-medium text-gray-900">
              {trade.remaining_shares ? formatLarge(trade.remaining_shares, 1) : '-'}
            </span>
          </div>
        </div>
      </div>
      <div className="flex gap-2">
        <div className="flex-1 border border-gray-200 rounded-md px-3 py-2">
          <div className="text-xs text-gray-500">Filing Date</div>
          <div className="text-sm font-medium text-gray-900">{formatTradeDate(trade.last_filing_date)}</div>
        </div>
        <div className="flex-1 border border-gray-200 rounded-md px-3 py-2">
          <div className="text-xs text-gray-500">Trade Date</div>
          <div className="text-sm font-medium text-gray-900">{formatTradeDate(trade.last_trade_date)}</div>
        </div>
      </div>
    </div>
  );
});

const MOCK_INSIDER_TRADES: InsidersTradeFiling[] = [
  {
    _id: 'mock-1',
    company_ticker: 'AAPL',
    company_name: 'Apple Inc.',
    company_cik: '0000320193',
    traded_shares: 50000,
    traded_share_price: 175.25,
    traded_value: 8762500,
    traded_percentage: 2.5,
    remaining_shares: 2050000,
    insider_names: 'John Smith',
    insider_titles: 'Chief Financial Officer',
    insider_titles_unique: 'Chief Financial Officer',
    last_filing_date: '2024-01-15',
    last_trade_date: '2024-01-12',
    insiders: [{ cik: '0001234567' }],
  } as InsidersTradeFiling,
  {
    _id: 'mock-2',
    company_ticker: 'MSFT',
    company_name: 'Microsoft Corporation',
    company_cik: '0000789019',
    traded_shares: -25000,
    traded_share_price: 380.5,
    traded_value: -9512500,
    traded_percentage: -1.8,
    remaining_shares: 1350000,
    insider_names: 'Jane Doe',
    insider_titles: 'Chief Technology Officer',
    insider_titles_unique: 'Chief Technology Officer',
    last_filing_date: '2024-01-14',
    last_trade_date: '2024-01-11',
    insiders: [{ cik: '0001234568' }],
  } as InsidersTradeFiling,
];

export const InsiderTradesWidget = () => {
  const session = useContext(SessionContext);
  const watchlistManager = session.getManager(WatchlistManager);
  const { hasEdgeSubscription: hasEdgeAccess, isEdgeSubscriptionLoading } = useBenzingaEdge();

  const [watchlists, setWatchlists] = useState<Watchlist[]>([]);
  const [insiderTrades, setInsiderTrades] = useState<InsidersTradeFiling[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const showEdgeCTA = !isEdgeSubscriptionLoading && !hasEdgeAccess;

  useEffect(() => {
    // Wait for the edge subscription check (and the underlying auth session) to
    // resolve before deciding whether to fetch or show the CTA. Without this
    // gate, fetches fire before auth is ready and the effect doesn't re-run
    // when subscribers' state flips from "unknown" to "has access".
    if (isEdgeSubscriptionLoading) {
      return;
    }

    if (showEdgeCTA) {
      setIsLoading(false);
      return;
    }

    const loadData = async () => {
      setIsLoading(true);

      try {
        const watchlistResult = await watchlistManager.getWatchlists();
        if (watchlistResult.ok && watchlistResult.ok.length > 0) {
          setWatchlists(watchlistResult.ok);

          const symbols: string[] = [];
          watchlistResult.ok.forEach(watchlist => {
            watchlist.symbols.forEach(symbol => {
              if (!symbols.includes(symbol.symbol)) {
                symbols.push(symbol.symbol);
              }
            });
          });

          if (symbols.length > 0) {
            const tradesResult = await getWatchlistInsiderFilingsFromSecCalendar(session.getSession(), symbols);
            if (tradesResult.ok) {
              setInsiderTrades(tradesResult.ok.slice(0, 10));
            }
          }
        }
      } catch (error) {
        setIsLoading(false);
        console.error('Error fetching insider trades:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [watchlistManager, showEdgeCTA, isEdgeSubscriptionLoading]);

  const renderEdgeCTA = () => (
    <div className="relative h-full overflow-hidden">
      <div className="blur-sm pointer-events-none">
        <div className="flex flex-col gap-3 h-full overflow-y-auto">
          {MOCK_INSIDER_TRADES.map(trade => (
            <TradeCard key={trade._id} trade={trade} />
          ))}
        </div>
      </div>
      <div className="absolute inset-0 bg-blue-50/80 backdrop-blur-[2px] flex items-center justify-center">
        <div className="text-center px-6 max-w-md">
          <h3 className="text-bzgray-900 text-lg font-bold leading-tight uppercase mb-2">Unlock Insider Trades</h3>
          <p className="text-bzgray-600 text-sm mb-4 leading-relaxed">
            Get Benzinga Edge to track insider trading activity on your watchlist stocks and discover what company
            insiders are buying and selling.
          </p>
          <Button as="a" variant="flat-blue" className="uppercase" href={EDGE_CTA_URL}>
            Get Benzinga Edge
          </Button>
        </div>
      </div>
    </div>
  );

  const renderEmptyState = () => (
    <div className="relative h-full overflow-hidden">
      <div className="blur-sm pointer-events-none">
        <div className="flex flex-col gap-3 h-full overflow-y-auto">
          {MOCK_INSIDER_TRADES.map(trade => (
            <TradeCard key={trade._id} trade={trade} />
          ))}
        </div>
      </div>
      <div className="absolute inset-0 bg-blue-50/80 backdrop-blur-[2px] flex items-center justify-center">
        <div className="text-center px-6 max-w-md">
          <h3 className="text-bzgray-900 text-lg font-bold leading-tight uppercase mb-2">
            Create a Watchlist to Get Access
          </h3>
          <p className="text-bzgray-600 text-sm mb-4 leading-relaxed">
            Track your favorite stocks, news, insider trades, events, and the latest company info.
          </p>
          <Button as="a" variant="flat-blue" className="uppercase" href="/profile/portfolio?action=create-watchlist">
            Create a Watchlist
          </Button>
        </div>
      </div>
    </div>
  );

  const renderInsiderTradesContent = () => {
    if (insiderTrades.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center h-full px-6 text-center">
          <h3 className="text-bzgray-900 text-lg font-bold leading-tight uppercase mb-2">No Insider Trades Found</h3>
          <p className="text-bzgray-600 text-sm mb-4 leading-relaxed">
            None of your watchlist stocks have recent insider trading activity. Explore the latest stocks being
            transacted by company insiders.
          </p>
          <Button as="a" variant="flat-blue" className="uppercase" href="/sec/insider-trades">
            Explore Insider Trades
          </Button>
        </div>
      );
    }

    return (
      <div className="flex flex-col gap-3 h-full overflow-y-auto">
        {insiderTrades.map(trade => (
          <TradeCard key={trade._id} trade={trade} />
        ))}
      </div>
    );
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="flex flex-col items-center justify-center h-full">
          <span>
            <Spinner />
          </span>
          <div className="text-gray-500 text-sm mt-3">Loading insider trades...</div>
        </div>
      );
    }

    if (showEdgeCTA) {
      return renderEdgeCTA();
    }

    if (watchlists.length === 0) {
      return renderEmptyState();
    }

    return renderInsiderTradesContent();
  };

  return (
    <ErrorBoundary name="insider-trades-widget">
      <BlockLayout
        title="INSIDER TRADES"
        actionLabel="View All"
        actionLink="/sec/insider-trades"
        showChevron
        variant="default"
      >
        <div className="h-[400px]">{renderContent()}</div>
      </BlockLayout>
    </ErrorBoundary>
  );
};

export default InsiderTradesWidget;
